import { ReactNode, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { IMessageData, INotificationState } from '@/types';
import { Toast } from '@/widgets';
import NotificationManager from '@/utils/NotificationManager';
import { RootState } from '@/redux/ducks';
import { setNotifications } from '@/redux/ducks/notifications/action';

interface IWidgetParams {
	toastTemplate: (messages: IMessageData[]) => ReactNode;
	showNotificationIcon?: boolean;
}

const TOAST_ID = 'notification-toast';

export const useNotificationsWidget = ({
	toastTemplate,
	showNotificationIcon = false,
}: IWidgetParams) => {
	const [showNotificationsDrawer, setShowNotificationsDrawer] = useState(false);
	const dispatch = useDispatch();
	const notifications = useSelector(
		(state: RootState) => state.notifications as INotificationState,
	);

	const unreadNotificationCount = notifications.unread?.length ?? 0;

	const { merchantUserId = '' } = useSelector(
		(state: RootState) => state.userProfile,
	);

	const { data: chimeraConfigData = {} } = useSelector(
		(state: RootState) => state.userConfig.chimeraConfig,
	);

	const notificationEnabled =
		merchantUserId &&
		showNotificationIcon &&
		chimeraConfigData.NOTIFICATIONS_ENABLED;

	const isDrawerOpen = useRef(false);

	function updateNotificationsStore({
		read = [],
		unread = [],
	}: Partial<INotificationState> = {}) {
		dispatch(
			setNotifications({
				read,
				unread,
			}),
		);
	}

	function handleNotificationsReceived({
		read,
		unread,
		newNotifications = [],
	}: INotificationState) {
		updateNotificationsStore({
			read,
			unread,
		});
		if (!isDrawerOpen.current && !isEmpty(newNotifications)) {
			const template = toastTemplate(newNotifications);
			if (template) {
				Toast.info(template, {
					key: TOAST_ID,
					onClick: () => {
						setShowNotificationsDrawer(true);
						Toast.close(TOAST_ID);
					},
				});
			}
		}
	}

	function handleNotificationDrawerClose() {
		setShowNotificationsDrawer(false);
		// mark all notifications as read on drawer close
		NotificationManager.markAllRead();
	}

	function handleBellClick() {
		setShowNotificationsDrawer(true);
	}

	useEffect(() => {
		if (!notificationEnabled) return;
		// Avoiding re initializations due to re mounts is handled in `NotificationManager`
		NotificationManager.setNotificationsHandler(handleNotificationsReceived);
		NotificationManager.init(merchantUserId);
	}, []);

	useEffect(() => {
		isDrawerOpen.current = showNotificationsDrawer;
	}, [showNotificationsDrawer]);

	return {
		handleNotificationDrawerClose,
		unreadNotificationCount,
		notificationEnabled,
		handleBellClick,
		showNotificationsDrawer,
		notifications,
	};
};
